'use client';

import React, { useEffect, useState, useRef } from 'react';
import { EditorInstance } from 'novel';
import { TrashIcon } from '@heroicons/react/24/outline';

interface FloatingTableMenuProps {
    editor: EditorInstance | null;
}

/**
 * A floating context menu that appears above the active table
 * when the cursor is inside a table cell. Provides quick access
 * to table editing operations (add/delete rows and columns).
 */
export const FloatingTableMenu: React.FC<FloatingTableMenuProps> = ({ editor }) => {
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!editor) return;

        const updatePosition = () => {
            if (!editor.isActive('table')) {
                setIsVisible(false);
                return;
            }

            const { view, state } = editor;
            const { selection } = state;

            // Get the bounding box of the editor root area
            const editorRoot = view.dom.closest('.novel-editor-root');
            if (!editorRoot) {
                setIsVisible(false);
                return;
            }
            const editorRect = editorRoot.getBoundingClientRect();

            try {
                // Get the coordinates of the current cursor / selection start
                const coords = view.coordsAtPos(selection.from);

                // Calculate position relative to the editor container
                const menuWidth = menuRef.current?.offsetWidth || 300;

                // Position above the current cursor
                const left = coords.left - editorRect.left - (menuWidth / 2) + 20;
                const top = coords.top - editorRect.top - 46; // Place above the cursor

                // Ensure it stays within bounds
                setPosition({
                    top: Math.max(0, top),
                    left: Math.max(8, left),
                });
                setIsVisible(true);
            } catch (e) {
                // Ignore coordsAtPos errors (sometimes happens during rapid typing)
                setIsVisible(false);
            }
        };

        // We need to delay slightly so the DOM has updated and coordsAtPos is accurate
        const debouncedUpdate = () => {
            setTimeout(updatePosition, 10);
        };

        // Listen for all possible editor updates
        editor.on('selectionUpdate', debouncedUpdate);
        editor.on('transaction', debouncedUpdate);
        editor.on('update', debouncedUpdate);

        // Initial check
        debouncedUpdate();

        return () => {
            editor.off('selectionUpdate', debouncedUpdate);
            editor.off('transaction', debouncedUpdate);
            editor.off('update', debouncedUpdate);
        };
    }, [editor]);

    if (!editor || !isVisible || !position) return null;

    const buttonClass = "px-2 py-1 text-xs font-medium rounded hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer";

    return (
        <div
            ref={menuRef}
            className="absolute z-50 flex items-center gap-0.5 bg-white rounded-lg shadow-lg border border-gray-200 px-1 py-1"
            style={{
                top: position.top,
                left: position.left,
            }}
            onMouseDown={(e) => {
                // Prevent toolbar buttons from stealing focus from the editor
                e.preventDefault();
            }}
        >
            <button
                className={`${buttonClass} text-blue-600 hover:bg-blue-50`}
                onClick={() => editor.chain().focus().addColumnBefore().run()}
                title="Add Column Before"
            >
                + Col ←
            </button>
            <button
                className={`${buttonClass} text-blue-600 hover:bg-blue-50`}
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                title="Add Column After"
            >
                + Col →
            </button>
            <div className="w-px h-4 bg-gray-200" />
            <button
                className={`${buttonClass} text-blue-600 hover:bg-blue-50`}
                onClick={() => editor.chain().focus().addRowBefore().run()}
                title="Add Row Above"
            >
                + Row ↑
            </button>
            <button
                className={`${buttonClass} text-blue-600 hover:bg-blue-50`}
                onClick={() => editor.chain().focus().addRowAfter().run()}
                title="Add Row Below"
            >
                + Row ↓
            </button>
            <div className="w-px h-4 bg-gray-200" />
            <button
                className={`${buttonClass} text-red-500 hover:bg-red-50`}
                onClick={() => editor.chain().focus().deleteColumn().run()}
                title="Delete Column"
            >
                − Col
            </button>
            <button
                className={`${buttonClass} text-red-500 hover:bg-red-50`}
                onClick={() => editor.chain().focus().deleteRow().run()}
                title="Delete Row"
            >
                − Row
            </button>
            <div className="w-px h-4 bg-gray-200" />
            <button
                className={`${buttonClass} text-red-600 hover:bg-red-50`}
                onClick={() => editor.chain().focus().deleteTable().run()}
                title="Delete Table"
            >
                <TrashIcon className="h-3.5 w-3.5" />
            </button>
        </div>
    );
};

export default FloatingTableMenu;
